/**
 * Speed Plus article behaviour.
 *
 * Three jobs, all driven by the article body that Shopify renders from the
 * blog editor, so nothing here has to be configured per post:
 *
 *   1. <sp-article-toc> reads the headings out of the body, gives each one a
 *      stable id, and builds the contents list. A scroll listener keeps the
 *      current entry marked.
 *   2. The reading progress bar tracks how far through the body the reader is.
 *   3. Small repairs the editor cannot make: wide tables get a scroll box,
 *      images below the fold get lazy loading.
 *
 * Everything degrades quietly. With the script blocked the article still reads
 * top to bottom, it just loses the contents rail.
 */
(function () {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Turns heading text into an id that survives being pasted into a link. */
  function slugify(text) {
    const slug = String(text)
      .toLowerCase()
      .trim()
      .replace(/[‘’“”]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
    return slug || 'section';
  }

  function onFrame(fn) {
    let queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  /**
   * The contents rail.
   *
   * Markup contract: an empty <ol data-toc-list> inside a <details> the theme
   * renders open. The list is filled here; if the article has too few headings
   * to be worth indexing the whole column removes itself and the grid closes up.
   */
  class SpArticleToc extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-toc-list]');
      this.disclosure = this.querySelector('details');
      this.body = document.querySelector(this.getAttribute('for') || '[data-article-body]');
      if (!this.list || !this.body) return;

      const levels = this.getAttribute('levels') === 'h2h3' ? 'h2, h3' : 'h2';
      const minimum = parseInt(this.getAttribute('minimum'), 10) || 2;

      this.headings = this.collect(levels);
      if (this.headings.length < minimum) {
        this.remove();
        document.querySelector('[data-sp-article]')?.setAttribute('data-toc', 'empty');
        return;
      }

      this.build();
      this.links = Array.from(this.list.querySelectorAll('a'));

      this.sync = onFrame(this.mark.bind(this));
      window.addEventListener('scroll', this.sync, { passive: true });
      window.addEventListener('resize', this.sync, { passive: true });
      this.addEventListener('click', this.jump.bind(this));

      this.collapseOnSmallScreens();
      this.mark();
    }

    disconnectedCallback() {
      if (!this.sync) return;
      window.removeEventListener('scroll', this.sync);
      window.removeEventListener('resize', this.sync);
    }

    /** Headings in document order, each guaranteed a unique id. */
    collect(levels) {
      const used = new Set();
      return Array.from(this.body.querySelectorAll(levels))
        .filter((node) => node.textContent.trim().length > 0)
        .map((node) => {
          let id = node.id || slugify(node.textContent);
          let n = 2;
          while (used.has(id)) id = slugify(node.textContent) + '-' + n++;
          used.add(id);
          node.id = id;
          return { id: id, node: node, text: node.textContent.trim(), level: node.tagName.toLowerCase() };
        });
    }

    build() {
      const fragment = document.createDocumentFragment();
      this.headings.forEach((heading) => {
        const item = document.createElement('li');
        item.className = 'sp-toc__item sp-toc__item--' + heading.level;
        const link = document.createElement('a');
        link.className = 'sp-toc__link';
        link.href = '#' + heading.id;
        link.textContent = heading.text;
        item.appendChild(link);
        fragment.appendChild(item);
      });
      this.list.appendChild(fragment);
    }

    /**
     * The entry for the heading the reader has most recently passed. Measured
     * against a line a little below the sticky header rather than the very top
     * of the viewport, so a heading counts as reached once it is comfortably
     * on screen.
     */
    mark() {
      if (!this.links || !this.links.length) return;
      const line = window.innerHeight * 0.28;
      let current = 0;

      for (let i = 0; i < this.headings.length; i++) {
        if (this.headings[i].node.getBoundingClientRect().top <= line) current = i;
        else break;
      }

      // At the very bottom the last section wins, however short it is.
      const atEnd = window.innerHeight + window.scrollY >= document.body.scrollHeight - 8;
      if (atEnd) current = this.headings.length - 1;

      if (current === this.active) return;
      this.active = current;

      this.links.forEach((link, i) => {
        const on = i === current;
        link.classList.toggle('is-current', on);
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    /** Smooth jump, then close the list again on the layouts where it overlays. */
    jump(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', link.hash);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });

      if (this.disclosure && this.matchesCompact()) this.disclosure.open = false;
    }

    matchesCompact() {
      const at = parseInt(this.getAttribute('compact-below'), 10) || 1000;
      return window.innerWidth < at;
    }

    /** The list is open by default so it renders without the script. */
    collapseOnSmallScreens() {
      if (this.disclosure && this.matchesCompact()) this.disclosure.open = false;
    }
  }

  if (!customElements.get('sp-article-toc')) {
    customElements.define('sp-article-toc', SpArticleToc);
  }

  /** How far through the article body the reader is, as a 0 to 1 scale. */
  function readingProgress() {
    const bar = document.querySelector('[data-read-progress]');
    const body = document.querySelector('[data-article-body]');
    if (!bar || !body) return;

    const update = onFrame(function () {
      const box = body.getBoundingClientRect();
      const travelled = -box.top;
      const total = box.height - window.innerHeight;
      const ratio = total <= 0 ? (travelled > 0 ? 1 : 0) : travelled / total;
      bar.style.setProperty('--sp-progress', Math.min(1, Math.max(0, ratio)).toFixed(4));
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /**
   * Repairs on content the blog editor produces. A pasted table is usually
   * wider than the column, and images further down the page should not block
   * the first paint.
   */
  function tidyBody() {
    const body = document.querySelector('[data-article-body]');
    if (!body) return;

    body.querySelectorAll('table').forEach(function (table) {
      if (table.parentElement && table.parentElement.classList.contains('sp-article__scroll')) return;
      const box = document.createElement('div');
      box.className = 'sp-article__scroll';
      box.setAttribute('tabindex', '0');
      box.setAttribute('role', 'region');
      table.parentNode.insertBefore(box, table);
      box.appendChild(table);
    });

    body.querySelectorAll('img').forEach(function (img, index) {
      if (!img.hasAttribute('loading') && index > 0) img.setAttribute('loading', 'lazy');
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
    });
  }

  /** Copy this article's address. Falls back to selecting nothing and saying so. */
  function copyLink() {
    document.addEventListener('click', function (event) {
      const button = event.target.closest('[data-copy-link]');
      if (!button) return;
      event.preventDefault();

      const url = button.getAttribute('data-copy-link') || window.location.href;
      const done = button.getAttribute('data-copied-label') || 'Copied';
      const original = button.getAttribute('data-label') || button.getAttribute('aria-label') || '';

      const settle = function () {
        button.classList.add('is-copied');
        button.setAttribute('aria-label', done);
        window.setTimeout(function () {
          button.classList.remove('is-copied');
          if (original) button.setAttribute('aria-label', original);
        }, 2000);
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(settle).catch(function () {});
      }
    });
  }

  function start() {
    tidyBody();
    readingProgress();
    copyLink();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
